import type { GetCurrencyConversionByCodeQuery } from '../Card.graphql.generated';
import { useGetCurrencyConversionByCodeQuery } from '../Card.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useStationeryCurrencies } from '@shared/utils/currency/useStationeryCurrencies';

/**
 * Base currency: USD
 */
export type CurrencyRateData = Readonly<{
  /** Exchange rate from USD to this currency */
  exchangeRate: number;
  /** Price increase multiplier (e.g., 1.10 = 10% increase, 1.20 = 20% increase) */
  taxesAndFeesMultiplier: number;
  /** Fixed cost per card in minor units */
  fixedCostPerCardInMinorUnits: number;
}>;

export const useCurrencyConversionRate = (currency: string = ''): { data: GetCurrencyConversionByCodeQuery['currencyConversionByCode'] | undefined; loading: boolean } => {
  const currencies = useStationeryCurrencies();
  const needsRate = !!currency && currency !== DEFAULT_CURRENCY;
  const isSold = !!currencies?.some(({ currencyCode }) => currencyCode === currency);

  const { data, loading } = useGetCurrencyConversionByCodeQuery({
    variables: { currencyCode: currency },
    batchMode: 'fast',
    // Skip if currency is not set or is the default currency.
    // There's no point in fetching the currency conversion rate for the default currency.
    //
    // Also skip anything card_service does not sell in (and everything until its list loads):
    // asking it for e.g. ZAR is the request that produced the blank-price incident. Callers
    // normalize via `useStationeryCurrency`, so this is a backstop for any caller that passes a
    // raw currency through.
    skip: !needsRate || !isSold,
  });

  // Still loading while the list is in flight: it decides whether to fetch, and can still move the currency
  // (e.g. a USD geo to PLN by region), so a USD caller waits too.
  return { data: data?.currencyConversionByCode, loading: loading || !currencies };
};
